import { useCallback, useRef } from 'react';
import { InlineButton } from '@picsart/design-system/components/InlineButton';
import { IconSoundOff } from '@picsart/design-system/foundation/icons/IconSoundOff';
import { IconSoundOn } from '@picsart/design-system/foundation/icons/IconSoundOn';

import { useAppStore, type IAppStore } from '@/store';
import usePreviewMedia from '@/hooks/usePreviewMedia';
import useVideoPlayback from '@/hooks/useVideoPlayback';
import useStyles from './styles';
import type { PreviewSectionProps } from './types';

const selector = (store: IAppStore) => store.selectedPreset;

/**
 * Renders the selected preset's preview media (image or video). Owns its
 * own video element ref and playback state — the parent only provides
 * layout-aware sizing.
 */
const PreviewSection = ({
  previewAspectRatio,
  hasUploadedMedia,
  onPreviewImageLoad,
}: PreviewSectionProps) => {
  const classes = useStyles();
  const selectedPreset = useAppStore(selector);
  const previewMedia = usePreviewMedia(selectedPreset);
  const videoRef = useRef<HTMLVideoElement>(null);

  const {
    isVideoMuted,
    hasAudio,
    handleVideoMuteToggle,
    handleVideoLoadedMetadata,
  } = useVideoPlayback({
    videoRef,
    selectedPreset,
    isVideo: previewMedia?.isVideo ?? false,
  });

  const handleCanPlay = useCallback(() => {
    videoRef.current?.play().catch(() => {});
  }, []);

  if (!previewMedia) return null;

  const title = selectedPreset?.title;

  return (
    <div
      className={classes.previewContainer}
      style={{ aspectRatio: hasUploadedMedia ? previewAspectRatio : 1 }}
    >
      {previewMedia.isVideo ? (
        <>
          {previewMedia.poster && (
            <img
              className={classes.previewVideoBackground}
              src={previewMedia.poster}
              alt="Blurred video background"
              aria-hidden="true"
            />
          )}
          <video
            ref={videoRef}
            className={classes.previewVideo}
            muted={isVideoMuted}
            poster={previewMedia.poster}
            autoPlay
            loop
            playsInline
            preload="auto"
            title="Preview video"
            onLoadedMetadata={handleVideoLoadedMetadata}
            onCanPlay={handleCanPlay}
            controls={false}
            disableRemotePlayback
            disablePictureInPicture
          >
            <source src={previewMedia.url} type="video/mp4" />
            <track kind="captions" />
          </video>
          {hasAudio && (
            <span className={classes.audioToggleWrapper}>
              <InlineButton
                size="large"
                centerIcon={isVideoMuted ? IconSoundOff : IconSoundOn}
                className={classes.iconButton}
                isWithoutBorder
                isWithoutBackground
                onClick={handleVideoMuteToggle}
                aria-label={isVideoMuted ? 'Unmute' : 'Mute'}
                dataTestId="preview-audio-toggle"
              />
            </span>
          )}
        </>
      ) : (
        <>
          <img
            className={classes.previewImageBackground}
            src={previewMedia.url}
            alt="Blurred image background"
            aria-hidden="true"
          />
          <img
            className={classes.previewMedia}
            src={previewMedia.url}
            alt={title || 'Preview'}
            onLoad={onPreviewImageLoad}
          />
        </>
      )}
      {title && <div className={classes.titleOverlay}>{title}</div>}
    </div>
  );
};

export default PreviewSection;
